import type { StrategyStatus } from "../api/types";
import { parseMode, rowAction } from "./strategies";

/**
 * Какие строки журнала стратегий поднимаются карточкой в полосу алертов.
 *
 * # Почему полоса, а не только лента
 *
 * Ступень `alert` даёт оператору окно в 17–46 секунд: строка рождается почти в
 * конце пятиминутки, а кнопка живёт ровно до её конца. Лента отвечает на вопрос
 * «что было», и смотреть в неё именно в эти секунды некому — тот же довод, по
 * которому в полосе висят карточки китов.
 *
 * Ступень `alert` И ТОЛЬКО ОНА: см. отбор в strategyAlerts — в `auto` спрашивать
 * оператора не о чем.
 *
 * # Почему отбор здесь, а не в компоненте
 *
 * Он состоит из четырёх правил, и каждое стоит денег, если ошибиться. Чистая
 * функция проверяется таблицей случаев; та же логика внутри компонента
 * проверялась бы рендером, то есть почти никогда.
 */

/** Строка журнала, из которой может выйти карточка. Общая часть трёх стратегий. */
export interface AlertRow {
  id: string;
  asset?: string;
  window_end: string;
  /**
   * Половинки клетки. У стратегии без клетки (семьи) их нет, а у расхождения
   * есть только первая: лесенки цен там не заведено.
   */
  source?: string;
  limit_price?: string;
  /**
   * Правило измерения, которым строка придержана (contested, flip…). Пусто или
   * отсутствует — строка чистая; у расхождения правил измерения нет вовсе.
   */
  rule?: string;
  /**
   * Клетка сетки у котировки когорты: три ЧИСЛА вместо пары «источник + цена».
   *
   * Своя форма, потому что и клетка своя: у котировки её задают уверенность в
   * базисных пунктах, ступень ставки в целых долларах и число подтверждений, а
   * источник у неё один и в адрес строки не входит.
   */
  consensus_bp?: number;
  stake_step_usd?: number;
  agree_n?: number;
}

/** Что показывает одна карточка полосы. */
export interface StrategyAlert {
  /** Ключ звонка и списка: строка уникальна в пределах своей стратегии. */
  key: string;
  strategy: string;
  id: string;
  asset: string;
  windowEnd: string;
  /** Клетка строкой «источник@цена» — только там, где она есть. */
  cell?: string;
}

/**
 * Попадает ли строка в ТОРГУЮЩУЮ клетку журнала.
 *
 * По одному окну почти решённое пишет до десяти строк (два источника × лесенка
 * из пяти ступеней), а ордер разрешён ровно по одной клетке — остальные строки
 * остаются замером, и команда по ним отвергается правилом `cell`. Карточка по
 * каждой из десяти научила бы оператора угадывать, какая кнопка настоящая.
 *
 * Пустая клетка означает «клетки у стратегии нет» (семьи: одна строка на окно и
 * сторону), и тогда годится любая строка. Отсутствие клетки при её ожидаемом
 * наличии сюда не приезжает: трейдер шлёт ноту только когда клетка есть.
 *
 * Клетка БЕЗ «@» — это клетка из одного источника, и так отвечает расхождение:
 * лесенки цен у него нет, а вторую ось окна выбирает сам сигнал. Раньше такая
 * нота читалась как испорченная и гасила КАЖДУЮ карточку — молча, потому что
 * пустой список карточек и список из нуля подходящих выглядят одинаково.
 *
 * Клетка из ТРЁХ ЧИСЕЛ через дефис («6500-1500-2») — это котировка когорты:
 * уверенность в базисных пунктах, ступень ставки в долларах, число подтверждений.
 * Форму задаёт Cell.Code() в трейдере, и та же строка стои́т в переключателе
 * отчёта тейкера, так что второго её определения в интерфейсе нет.
 *
 * Цена сравнивается ЧИСЛОМ: «0.999» из настройки и «0.999000» из NUMERIC — одна
 * и та же ступень, а строковое равенство развело бы их и погасило бы карточки
 * молча. По тому же доводу числами сравниваются и три оси котировки.
 */
export function inCell(row: AlertRow, cell: string | undefined): boolean {
  if (!cell) return true;
  const axes = consensusCellAxes(cell);
  if (axes) {
    return (
      row.consensus_bp === axes[0] &&
      row.stake_step_usd === axes[1] &&
      row.agree_n === axes[2]
    );
  }
  const at = cell.lastIndexOf("@");
  if (at < 0) return row.source === cell;
  const source = cell.slice(0, at);
  const price = Number(cell.slice(at + 1));
  if (!Number.isFinite(price)) return false;
  return row.source === source && Number(row.limit_price) === price;
}

/**
 * Разбор клетки котировки: три целых через дефис либо null.
 *
 * null означает «это не та форма», а не «клетка испорчена»: разбор служит
 * РАЗЛИЧЕНИЮ форм, и вернув здесь нули, он погасил бы карточки почти решённого,
 * у которого клетка выглядит иначе.
 */
function consensusCellAxes(cell: string): [number, number, number] | null {
  // Регулярным выражением, а не разбором частей: три ЦЕЛЫХ и ровно три — это и
  // есть определение формы, и записать его одним местом дешевле, чем тремя
  // проверками, каждую из которых можно забыть.
  const m = /^(\d+)-(\d+)-(\d+)$/.exec(cell);
  if (!m) return null;
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

/** Строки одной стратегии вместе с тем, что про неё говорит трейдер. */
export interface StrategyFeed {
  strategy: string;
  status: StrategyStatus | undefined;
  rows: readonly AlertRow[];
}

/**
 * Карточки, по которым ЕСТЬ ЧТО НАЖАТЬ прямо сейчас.
 *
 * Четыре условия, и все четыре обязательны:
 *
 *   режим   — РОВНО `alert`, ни выше, ни ниже;
 *   окно    — ещё идёт, иначе вход обосновать нечем и трейдер снял бы заявку;
 *   клетка  — строка та самая, по которой разрешён ордер;
 *   правило — строка не придержана правилом измерения (contested, flip).
 *
 * # Почему `auto` полосу НЕ поднимает
 *
 * Полоса — это и есть ступень `alert`: она существует затем, чтобы спросить
 * оператора там, где подсистема сама не входит. В `auto` решение оператора не
 * нужно по определению — строку забирает автомат, и карточка предлагала бы
 * выбор, которого у человека нет, а звонок звал бы к экрану ради сделки, которая
 * уже сделана или уже отвергнута. Хуже того, нажатие в этот момент — это ВТОРОЙ
 * вход по тому же окну наперегонки с автоматом.
 *
 * Одного отсева придержанных строк здесь мало, и это проверено на живом стенде:
 * `decided` в `auto / taker` при 120 строках и НУЛЕ ордеров звонил тремя чистыми
 * карточками подряд. Причин, по которым автомат не поставил чистую строку, много
 * (свои ворота, лимиты, деньги), и все они — разговор с автоматом, а не с
 * оператором.
 *
 * Правило судит вход как таковой, а не способ постановки: автомат такую строку
 * не торгует, команда нажатия отвергает её тем же отказом (rowGuard) — карточка
 * со звонком приглашала бы в сделку, которую трейдер обязан отвергнуть.
 *
 * Кнопка в САМОЙ ленте (StrategyRowActions) при этом остаётся и в `auto`: там
 * оператор смотрит сам и правит автомат осознанно. Полоса же приходит незваной и
 * звонит — у неё право на внимание уже, чем у строки журнала.
 *
 * Порядок — по концу окна, ближайшее сверху: карточка, которой осталось десять
 * секунд, важнее той, у которой сорок, и решать это оператор не должен.
 */
export function strategyAlerts(feeds: readonly StrategyFeed[], now: number): StrategyAlert[] {
  const out: StrategyAlert[] = [];
  for (const feed of feeds) {
    const cell = feed.status?.live_cell;
    // Режим спрашивается ОДИН раз на журнал: он свойство подсистемы, а не строки.
    if (parseMode(feed.status?.mode).mode !== "alert") continue;
    for (const row of feed.rows) {
      // rowAction — по-прежнему он: срок окна с fail-closed по нечитаемой дате
      // живёт в одном месте, и второе его определение здесь разошлось бы с тем,
      // по которому гаснет кнопка.
      if (rowAction(feed.status?.mode, row.window_end, now) !== "take") continue;
      if (!inCell(row, cell)) continue;
      if (row.rule) continue;
      const card: StrategyAlert = {
        key: `${feed.strategy}:${row.id}`,
        strategy: feed.strategy,
        id: row.id,
        asset: row.asset ?? "",
        windowEnd: row.window_end,
      };
      // Клетка ставится ТОЛЬКО когда она есть: `exactOptionalPropertyTypes`
      // отличает отсутствующее поле от поля со значением undefined, и это
      // различие здесь содержательное — у семей клетки нет вовсе.
      if (cell) card.cell = cell;
      out.push(card);
    }
  }
  return out.sort((a, b) => Date.parse(a.windowEnd) - Date.parse(b.windowEnd));
}

/** Какие карточки ещё не звучали. Ключ — стратегия и строка. */
export function unannouncedAlerts(
  live: readonly StrategyAlert[],
  announced: ReadonlySet<string>,
): string[] {
  return live.map((a) => a.key).filter((key) => !announced.has(key));
}
